import React, { useEffect, useRef, useState } from "react";
import { X, Eraser, Check } from "lucide-react";

/**
 * iter-169h — Signature capture dialog.
 *
 * Full-screen modal with a large `<canvas>` sized to the viewport that
 * accepts finger, stylus, or mouse input via pointer events. The final
 * signature is exported as a transparent-background PNG data URL and
 * handed back to the caller so it can be stamped onto the schedule
 * PDF's Section E "Signature of applicant" line.
 */
export default function SignaturePadDialog({ open, onOpenChange, onSave, initialSaveToProfile = false }) {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastRef = useRef(null);
    const [hasInk, setHasInk] = useState(false);
    const [saveToProfile, setSaveToProfile] = useState(initialSaveToProfile);

    useEffect(() => {
        if (!open) return;
        const c = canvasRef.current;
        if (!c) return;
        // Match backing store to CSS size for crisp lines on tablets.
        const rect = c.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        c.width = Math.floor(rect.width * dpr);
        c.height = Math.floor(rect.height * dpr);
        const ctx = c.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.strokeStyle = "#0a1128";
        ctx.lineWidth = 2.2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        setHasInk(false);
    }, [open]);

    const getPos = (e) => {
        const c = canvasRef.current;
        const rect = c.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onPointerDown = (e) => {
        e.preventDefault();
        canvasRef.current.setPointerCapture(e.pointerId);
        drawingRef.current = true;
        lastRef.current = getPos(e);
    };
    const onPointerMove = (e) => {
        if (!drawingRef.current) return;
        const p = getPos(e);
        const ctx = canvasRef.current.getContext("2d");
        const l = lastRef.current;
        ctx.beginPath();
        ctx.moveTo(l.x, l.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastRef.current = p;
        if (!hasInk) setHasInk(true);
    };
    const onPointerUp = () => {
        drawingRef.current = false;
        lastRef.current = null;
    };

    const clearPad = () => {
        const c = canvasRef.current;
        const ctx = c.getContext("2d");
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.restore();
        setHasInk(false);
    };

    const submit = () => {
        if (!hasInk) return;
        // Crop transparent margins so the signature fills the backend
        // rect without extra whitespace on top.
        const c = canvasRef.current;
        const ctx = c.getContext("2d");
        const { width, height } = c;
        const img = ctx.getImageData(0, 0, width, height);
        let minX = width, minY = height, maxX = 0, maxY = 0;
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const a = img.data[(y * width + x) * 4 + 3];
                if (a > 20) {
                    if (x < minX) minX = x;
                    if (y < minY) minY = y;
                    if (x > maxX) maxX = x;
                    if (y > maxY) maxY = y;
                }
            }
        }
        if (maxX < minX || maxY < minY) return;
        const pad = 6;
        minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
        maxX = Math.min(width - 1, maxX + pad); maxY = Math.min(height - 1, maxY + pad);
        const cropW = maxX - minX + 1;
        const cropH = maxY - minY + 1;
        const off = document.createElement("canvas");
        off.width = cropW; off.height = cropH;
        off.getContext("2d").drawImage(c, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
        onSave(off.toDataURL("image/png"), saveToProfile);
        onOpenChange(false);
    };

    if (!open) return null;
    return (
        <div
            data-testid="signature-pad-dialog"
            className="fixed inset-0 z-[120] bg-[#111] flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}
        >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                    <div>
                        <div className="text-sm font-bold text-slate-900">Sign here</div>
                        <div className="text-xs text-slate-500">
                            Use your finger or stylus. The signature will be stamped on Section E of the schedule.
                        </div>
                    </div>
                    <button
                        data-testid="btn-close-signature-pad"
                        onClick={() => onOpenChange(false)}
                        className="p-2 rounded hover:bg-slate-100"
                    >
                        <X className="w-4 h-4 text-slate-600" />
                    </button>
                </div>
                <div className="px-4 pt-4">
                    <div
                        className="relative bg-slate-50 rounded-md border border-slate-300"
                        style={{ height: 260 }}
                    >
                        <canvas
                            ref={canvasRef}
                            data-testid="signature-canvas"
                            className="absolute inset-0 w-full h-full touch-none"
                            onPointerDown={onPointerDown}
                            onPointerMove={onPointerMove}
                            onPointerUp={onPointerUp}
                            onPointerCancel={onPointerUp}
                            onPointerLeave={onPointerUp}
                        />
                        {!hasInk && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <span className="text-slate-400 italic text-sm">Draw signature here</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                        <button
                            data-testid="btn-clear-signature"
                            onClick={clearPad}
                            disabled={!hasInk}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Eraser className="w-4 h-4" />
                            Clear
                        </button>
                        <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                data-testid="chk-save-signature-to-profile"
                                checked={saveToProfile}
                                onChange={(e) => setSaveToProfile(e.target.checked)}
                                className="w-4 h-4 accent-[#1a3fd1]"
                            />
                            Save for future schedules
                        </label>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onOpenChange(false)}
                            className="px-4 py-2 rounded text-sm font-semibold text-slate-700 hover:bg-slate-100"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="btn-save-signature"
                            onClick={submit}
                            disabled={!hasInk}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[#1a3fd1] text-white text-sm font-semibold hover:bg-[#1a3fd1]/80 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Check className="w-4 h-4" />
                            Save signature
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
